import { Chart } from 'components/Chart/Chart'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { COLORS } from 'modules/Statistics/colors'
import StatisticsLayout from 'modules/Statistics/subcomponents/Layout'
import { ChartLegend } from 'modules/Statistics/subcomponents/Layout/ChartLegend'
import styles from 'modules/Statistics/statistics.module.scss'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Statistics/Main/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import { Button, Grid } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import Switch from 'components/Switch'
import STATISTICS_API from 'api/statistics/v2'
import TableComponent from './subcomponents/TableComponent'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PageHeader from 'components/PageHeader'
import useChangeChartType from 'modules/Statistics/hooks/useChangeChartType'
import GridSpinner from 'components/UI/GridSpinner'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyStatistics } from 'images/illustration/empty-statistics.svg'

const StatisticsMain = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { fbs } = useBreakpoint()
  const [cfg, setCfg] = useLocalStorage('statisticsConfig', {
    showStatisticsChart: true,
  })

  const [chartVisible, setChartVisible] = useState(cfg.showStatisticsChart)

  const { chart, i18n, filters } = useSelector(({ statistics, app }) => ({
    ...statistics,
    ...app,
  }))

  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [userHasAccounts, setUserHasAccounts] = useState(false)
  const [isDemoMode, setIsDemoMode] = useLocalStorage('isDemoMode', true)

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    additionalFilterState: {
      ...filters,
      ...isDemoMode,
    },
    setAction: 'STATISTICS/SET_FILTER',
    nonClearableFilters: ['date_group_period', 'include_vat'],
    localStorageKey: 'main-statistics',
  })

  useEffect(() => {
    if (isDemoMode) {
      setIsFirstLoad(false)
      setUserHasAccounts(true)
      return
    }

    STATISTICS_API.fetchAccounts().then(({ response, error }) => {
      if (!error) {
        const { data = [] } = response

        filterManager.updateConfiguration({
          key: 'account_ids',
          val: {
            options: data
              .sort((a, b) => {
                const nameA = a.login.toLowerCase()
                const nameB = b.login.toLowerCase()

                if (nameA < nameB) {
                  return -1
                }
                if (nameA > nameB) {
                  return 1
                }

                // names must be equal
                return 0
              })
              .map(({ login, id, ...rest }) => ({
                ...rest,
                value: id,
                label: login,
              })),
          },
        })

        setUserHasAccounts(data.length > 0)
      }

      setIsFirstLoad(false)
    })
  }, [isDemoMode])

  const [filtersState, setAccountFiltersState] = useState(
    omit(filterManager.filters, ['include_vat']),
  )

  useEffect(() => {
    const omittedFilters = omit(filterManager.filters, ['include_vat'])
    !isEqual(filtersState, omittedFilters) && setAccountFiltersState(omittedFilters)
  }, [filterManager.filters])

  const changeChartType = useChangeChartType(chart)

  useEffect(
    () => filterManager.filters && dispatch({ type: 'STATISTICS/FETCH_CHART', isDemoMode }),
    [filterManager.filters, isDemoMode],
  )

  useEffect(() => {
    setCfg({ showStatisticsChart: chartVisible })
  }, [chartVisible])

  return (
    <>
      <Grid
        className={'margin-bottom-32'}
        justifyContent={fbs ? 'between' : 'start'}
        alignItems={'center'}
      >
        <PageHeader color={'Label-Primary'}>{t('common::nav_menu.statistics')}</PageHeader>

        <Switch
          label={t('statistics::demo_mode_switcher')}
          checked={isDemoMode}
          onChange={() => {
            setIsDemoMode(!isDemoMode)
          }}
          className={styles['demo-switcher']}
        />
      </Grid>

      {isFirstLoad && !isDemoMode ? (
        <GridSpinner />
      ) : userHasAccounts ? (
        <StatisticsLayout
          chartVisible={chartVisible}
          FilterPanel={
            filterManager.filters && (
              <FilterPanel
                filterManager={filterManager}
                className={styles['filter-panel']}
              />
            )
          }
          ChartComponent={
            <Chart
              chartHeaderClassName={styles['chart-header_tab-legend']}
              chartLegend={
                <ChartLegend
                  measure={chart.measure}
                  metrics={chart.metrics}
                  summary={chart.summary}
                  include_vat={filterManager.filters?.include_vat}
                  handleClick={(id) =>
                    dispatch({
                      type: 'STATISTICS/TOGGLE_METRIC_HIDDEN',
                      payload: { id },
                    })
                  }
                />
              }
              i18n={i18n}
              emptyDataLabel={{
                text: (
                  <div className={styles['chart__empty-text']}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: t('statistics::chart.empty_data.text', {
                          context: isDemoMode ? 'demo' : '',
                        }),
                      }}
                    />

                    {!isDemoMode && (
                      <Button
                        variant={'primary'}
                        size={'m'}
                        onClick={() => setIsDemoMode(true)}
                      >
                        {t('statistics::demo_mode_switcher')}
                      </Button>
                    )}
                  </div>
                ),
              }}
              colors={COLORS}
              className={styles['chart']}
              chartType={chart.chartType}
              items={chart.data}
              metrics={chart.metrics}
              loading={chart.loading}
              date_group_period={{
                value: filterManager.filters?.date_group_period ?? 'day',
                options: filterManager.configuration.date_group_period.options,
              }}
              include_vat={filterManager.filters?.include_vat}
              summary={chart.summary}
              measure={chart.measure}
              changeChartPeriod={(value) =>
                filterManager.updateFilter({
                  fieldName: 'date_group_period',
                  value: value,
                })
              }
              changeChartType={changeChartType}
            />
          }
          TableComponent={
            filterManager.filters && (
              <TableComponent
                chartVisible={chartVisible}
                setChartVisible={setChartVisible}
                filters={filterManager.filters}
                isDemoMode={isDemoMode}
              />
            )
          }
        />
      ) : (
        <EmptyResults
          image={<EmptyStatistics />}
          text={t('statistics::tracked_accounts_empty')}
        />
      )}
    </>
  )
}

StatisticsMain.propTypes = {}

export default StatisticsMain
